/** @forge-header
 * @id          cricketParser
 * @role        Cricket ticker parser | KXWT20MATCH match moneylines
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,TennisMatchInfo | kalshiTicker/helpers:parseFallback,splitCompetitors,stripTimePrefix
 * @downstream  kalshiTicker/parse
 * @exports     parseCricketMatch:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, TennisMatchInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, splitCompetitors, stripTimePrefix } from '../helpers';

/**
 * Cricket: KXWT20MATCH-26JUN041100PAKIRL-IRL
 * Country-vs-country moneyline with an HHMM start-time prefix after the date.
 * Country codes are displayed as-is (uppercase). Reuses the two-competitor
 * `fight` field for the matchup.
 */
export function parseCricketMatch(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) return parseFallback(raw);

  const mid = parts[1] ?? '';
  if (mid.length < 9) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  const outcome = parts[2] ?? null;
  const [c1, c2] = splitCompetitors(stripTimePrefix(mid.slice(7)), outcome);

  const fight: TennisMatchInfo = {
    player1: { playerCode: c1, displayName: c1 },
    player2: { playerCode: c2, displayName: c2 },
  };

  const shortLabel = outcome ? `Cricket: ${outcome} Win` : 'Cricket';
  const fullLabel = `Cricket: ${c1} vs ${c2}${outcome ? ` · ${outcome} Win` : ''}`;

  return {
    raw,
    category: 'cricket-match',
    series: 'KXWT20MATCH',
    date,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    fight,
    outcome,
    shortLabel,
    fullLabel,
  };
}
